import { AxiosInstance, AxiosResponse } from "axios";
import qs from "qs";
import { ApiResponse } from "../public-types";
import {
  CtSelfVisitGroupListReqParam,
  CtSelfVisitGroupListResponse,
  Visitor,
  VisitorDeleteParams,
  VisitorDeleteResponseData,
  VisitorGroupDetailParams,
  VisitorGroupDetailResponseData,
  VisitorGroupListParams,
  VisitorGroupListResponseData,
  VisitorInviteGroupPageListParams,
  VisitorInviteGroupPageListResponseData,
  VisitorListParams,
  VisitorListResponseData,
  VisitorPatchResponseData,
  VisitorQrResponseData,
  VisitorUpdateResponseData,
} from "./visitor-types";

/**
 * (무인증) TAC11. 방문자 QR 조회
 */
export async function getExternalVisitorQrAsync(
  axios: AxiosInstance,
  params?: {
    visitorKey: string;
  },
): Promise<AxiosResponse<ApiResponse<VisitorQrResponseData>>> {
  if (!params?.visitorKey) {
    throw Error("visitorKey is not found.");
  }
  return await axios.get(`/api/court/ct/external/visitor/${params.visitorKey}/qr`);
}

/**
 * TCT58. (무인증)방문 초대내역 정보 조회
 */
export async function getExternalVisitorAsync(
  axios: AxiosInstance,
  params?: {
    visitorKey?: string; // visitorKey : 방문신청 번호(visitApplyNumber) + 계약방문자 id(contractVisitorId) => 방문자 초대내역 정보 조회
    invitationKey?: string; // invitationKey : 초대자회원번호(memberNo) + 방문신청 번호(visitApplyNumber) => 방문초대자 초대내역 정보 조회
  },
): Promise<AxiosResponse<ApiResponse<Visitor>>> {
  let queryParams = {};
  // visitorKey 또는 invitationKey 으로 조회
  if (!params?.visitorKey && !params?.invitationKey) {
    throw Error("visitorKey or invitationKey is not found.");
  }
  if (params?.visitorKey) {
    queryParams = { ...queryParams, ...{ visitorKey: params.visitorKey } };
  } else if (params?.invitationKey) {
    queryParams = { ...queryParams, ...{ invitationKey: params.invitationKey } };
  }
  return await axios.get(`/api/court/ct/external/visitor/`, {
    params: { ...queryParams, revision: 1 },
  });
}

/**
 * TCT59. (무인증)방문자 등록차량 정보 수정
 */
export async function updateExternalVisitorAsync(
  axios: AxiosInstance,
  params?: {
    visitorKey: string;
    visitorCarNumber: string;
  },
): Promise<AxiosResponse<ApiResponse<VisitorUpdateResponseData>>> {
  if (!params?.visitorKey) {
    throw Error("visitorKey is not found.");
  }
  if (!params?.visitorCarNumber) {
    throw Error("visitorCarNumber is not found.");
  }
  return await axios.patch(`/api/court/ct/external/visitor/${params.visitorKey}/update`, {
    contractVisitor: {
      visitorCarNumber: params.visitorCarNumber,
    },
  });
}

/**
 * TCT57. (무인증)방문자용 개인정보 이용 약관 동의 저장
 */
export async function patchConsentAgreeAsync(
  axios: AxiosInstance,
  params?: {
    visitorKey: string;
    inputNumber: string;
  },
): Promise<AxiosResponse<ApiResponse<{ data: VisitorPatchResponseData }>>> {
  if (!params?.visitorKey) {
    throw Error("visitorKey is not found.");
  }
  return await axios.patch(`/api/court/ct/external/visitor/${params.visitorKey}/consent-agree`, {
    inputNumber: params.inputNumber,
    form: {
      revision: 1,
    },
  });
}

/**
 * TCT54. (무인증) 방문자 초대
 */
export async function postVisitorInvitationAsync(
  axios: AxiosInstance,
  params?: {
    encryptedContractManageId: string;
    encryptedBuildingId: string;
    visitorMobileNumber: string;
    visitPurpose: string;
    token: string;
  },
): Promise<AxiosResponse<ApiResponse<Visitor>>> {
  if (!params?.encryptedContractManageId) {
    throw Error("params.encryptedContractManageId is not found.");
  }
  if (!params?.encryptedBuildingId) {
    throw Error("params.encryptedBuildingId is not found.");
  }
  return await axios.post(
    `/api/court/ct/external/visitor/contract-manage/${params?.encryptedContractManageId}/building/${params?.encryptedBuildingId}/invite`,
    {
      contractVisitorList: [{ visitorMobileNumber: params?.visitorMobileNumber, visitPurpose: params?.visitPurpose }],
    },
    {
      headers: {
        "X-Recaptcha-Token": params!.token,
      },
    },
  );
}

/**
 * TCT50b. 방문자 목록 페이지 조회
 */
export async function getVisitorListAsync(
  axios: AxiosInstance,
  params?: VisitorListParams,
): Promise<AxiosResponse<ApiResponse<VisitorListResponseData>>> {
  return await axios.get<ApiResponse<VisitorListResponseData>>(`/api/court/ct/visitor/list`, {
    params,
    paramsSerializer: (params) => qs.stringify(params, { allowDots: true, encode: true }),
  });
}

/**
 * TCV51b. 방문자 그룹 목록 조회
 */
export async function getVisitorGroupInviteListAsync(
  axios: AxiosInstance,
  params?: VisitorGroupListParams,
): Promise<AxiosResponse<ApiResponse<VisitorGroupListResponseData>>> {
  if (!params?.reservationId && !params?.contractManageId && !params?.contractVisitorGroupIds) {
    throw Error(
      `reservationId: ${params?.reservationId}, contractManageId: ${params?.contractManageId}, contractVisitorGroupIds: ${params?.contractVisitorGroupIds} - at least one parameter is required.`,
    );
  }

  return await axios.get<ApiResponse<VisitorGroupListResponseData>>(`/api/court/ct/visitor-group/invite-list`, {
    params,
    paramsSerializer: (params) => qs.stringify(params, { allowDots: true, encode: true }),
  });
}

/**
 * TCV51. 방문자 그룹 상세 조회
 */
export async function getVisitorGroupDetailAsync(
  axios: AxiosInstance,
  params?: VisitorGroupDetailParams,
): Promise<AxiosResponse<ApiResponse<VisitorGroupDetailResponseData>>> {
  if (!params?.visitApplyNumber) {
    throw Error("visitApplyNumber is not found.");
  }

  return await axios.get<ApiResponse<VisitorGroupDetailResponseData>>(`/api/court/ct/visitor-group/${params.visitApplyNumber}/visitor-group`);
}

/**
 * TCV50c. 방문자 초대 그룹 목록 조회 (페이징)
 */
export async function getVisitorInviteGroupPageListAsync(
  axios: AxiosInstance,
  params?: VisitorInviteGroupPageListParams,
): Promise<AxiosResponse<ApiResponse<VisitorInviteGroupPageListResponseData>>> {
  if (!params?.contractManageId) {
    throw Error("contractManageId is not found.");
  }

  const { contractManageId, ...queryParams } = params;

  return await axios.get<ApiResponse<VisitorInviteGroupPageListResponseData>>(`/api/court/ct/visitor/contract-manage/${contractManageId}/page-list`, {
    params: queryParams,
    paramsSerializer: (params) => qs.stringify(params, { allowDots: true, encode: true }),
  });
}

/**
 * TCV60. 셀프등록 방문자 그룹 목록 조회
 * - 이용자 승인이 필요한 방문자 셀프 등록 그룹 목록을 파트너 단위로 조회
 * - 응답의 `memberApprovalStatusCount.APPROVAL_PENDING` 으로 미처리 건수 확인 가능
 */
export async function getSelfVisitGroupListAsync(
  axios: AxiosInstance,
  params?: {
    partnerId: string | number;
    query: CtSelfVisitGroupListReqParam;
  },
): Promise<AxiosResponse<ApiResponse<CtSelfVisitGroupListResponse>>> {
  if (params?.partnerId === undefined || params?.partnerId === null || params?.partnerId === "") {
    throw Error("partnerId is not found.");
  }
  if (!params?.query) {
    throw Error("query is not found.");
  }
  return await axios.get<ApiResponse<CtSelfVisitGroupListResponse>>(`/api/court/ct/self-visit/partner/${params.partnerId}/group-list`, {
    params: params.query,
    paramsSerializer: (p) => qs.stringify(p, { allowDots: true, encode: true }),
  });
}

/**
 * TCT53. 방문자 삭제(초대자)
 */
export async function patchVisitorDeleteAsync(
  axios: AxiosInstance,
  params?: VisitorDeleteParams,
): Promise<AxiosResponse<ApiResponse<VisitorDeleteResponseData>>> {
  if (!params?.inviteeMemberNo || !params?.contractVisitorIds) {
    throw Error("params not found");
  }
  return await axios.patch(`/api/court/ct/visitor/${params.inviteeMemberNo}/visitor-delete`, {
    contractVisitorIds: params.contractVisitorIds,
  });
}
